import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../config/firebase';

/**
 * Get a direct download URL for a file
 * Access is controlled by Firebase Storage rules checking user entitlements
 */
export async function getDownloadUrl(storagePath: string): Promise<string> {
  const fileRef = ref(storage, storagePath);
  const url = await getDownloadURL(fileRef);
  return url;
}

/**
 * Trigger file download from a signed URL
 */
export function downloadFile(url: string, filename?: string) {
  const link = document.createElement('a');
  link.href = url;
  if (filename) {
    link.download = filename;
  }
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Download a file with progress tracking (optional)
 */
export async function downloadFileWithProgress(
  url: string,
  filename: string,
  onProgress?: (progress: number) => void
): Promise<void> {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error('Download failed');
  }

  const contentLength = response.headers.get('content-length');
  const total = contentLength ? parseInt(contentLength, 10) : 0;

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Stream not available');
  }

  const chunks: BlobPart[] = [];
  let loaded = 0;

  while (true) {
    const { done, value } = await reader.read();

    if (done) break;

    chunks.push(value);
    loaded += value.length;

    if (onProgress && total > 0) {
      onProgress((loaded / total) * 100);
    }
  }

  // Create blob and download
  const blob = new Blob(chunks);
  const blobUrl = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Cleanup
  URL.revokeObjectURL(blobUrl);
}
